import toast from 'react-hot-toast';
import {useModal} from '@hooks';

import {
  useAppDispatch,
  useAppSelector,
  Meeting,
  MeetingRoom,
  getCanUserAddRoomToExistingMeeting,
  getDefaultBuildingId,
  getMeetingRoomsByMeetingId,
  updateMeeting,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {Button, IconButton} from '@molecules';
import {BigMapModal, CreateEventManualRoomSelectionCard, MeetingDetailsCard} from '@organisms';
import {Icon} from '@atoms';
import {useTranslation} from 'react-i18next';
import {MeetingRoomInfoFooter} from './MeetingRoomInfoFooter';
import {MeetingRoomMapView} from './MeetingRoomMapView';
import {getFormatedMeetingTime} from '@lib/store/src/util';
import {trackEvent} from '@lib/infrastructure';

type Props = {
  meeting: Meeting;
  time: Date;
};

export const MeetingTile = ({meeting, time}: Props) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  // for handling clicks
  const {openModal, closeModal, setModalPages, setBigPages} = useModal();

  const meetingRooms = useAppSelector((state) => getMeetingRoomsByMeetingId(state, meeting.id));
  const location = meetingRooms.at(0);
  const timeLabel = getFormatedMeetingTime(meeting, t);

  const defaultBuildingId = useAppSelector(getDefaultBuildingId);

  const canUserAddRoom = useAppSelector((state) => getCanUserAddRoomToExistingMeeting(state, meeting.id)) && !location;

  const handleInfoClick = () => {
    const modalCard = (
      <MeetingDetailsCard
        date={meeting.startDateTime}
        meeting={meeting}
      />
    );
    trackEvent(`Home_UpcomingMeetingsTile__ViewDetails`);
    setModalPages([modalCard]);
    openModal();
  };

  const handleAddRoomClick = () => {
    setModalPages([
      <CreateEventManualRoomSelectionCard
        defaultBuildingId={defaultBuildingId}
        origin="CalendarOverview_AdHocRoom"
        endDateTime={meeting.endDateTime}
        onClose={closeModal}
        onSelect={async (value: MeetingRoom) => {
          const {success} = await dispatch(
            withAsyncThunkErrorHandling(() =>
              updateMeeting({
                origin: 'AddRoomToExistingMeeting',
                meetingId: meeting.id,
                systemTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                rooms: [...meetingRooms.map((room) => room.email), value.email],
              }),
            ),
          );
          if (success) {
            closeModal();
            toast.success(t('meeting:AddMeetingRoomToMeetingSuccessMessage', {roomName: value.displayName}));
          }
        }}
        selectedRooms={meetingRooms.map((room) => room.email)}
        startDateTime={meeting.startDateTime}
      />,
    ]);

    trackEvent('Home_UpcomingMeetingsTile__AddRoomClicked');
    openModal();
  };

  const expandMap = () => {
    if (!location) return;

    const mapView = (
      <BigMapModal
        title="Meeting room"
        main={
          <MeetingRoomMapView
            room={location}
            interactive={true}
          />
        }
        footer={<MeetingRoomInfoFooter room={location} />}
      />
    );

    trackEvent('Home_UpcomingMeetingsTile__ExpandMap', {
      buildingName: location.buildingName,
      floorName: location.floorName,
      roomName: location.displayName,
    });

    setModalPages([mapView]);
    setBigPages([mapView]);
    openModal();
  };

  return (
    <div className="w-full">
      {location && (
        <button
          className="w-full h-full mb-5 max-h-64 border rounded-lg overflow-hidden"
          onClick={expandMap}>
          <MeetingRoomMapView room={location} />
        </button>
      )}

      <div className="flex w-full overflow-hidden">
        <div className="flex flex-col w-full">
          <button onClick={handleInfoClick}>
            <p className="font-semibold">{timeLabel}</p>
            <p className="line-clamp-2">{meeting.title}</p>
            {location && <p className="line-clamp-2">{location.displayName}</p>}
          </button>

          {!location && canUserAddRoom && (
            <div>
              <Button
                className="grid grid-cols-[auto_1fr] mt-3"
                aria-label={t('GetInformationAboutYourUpcomingMeeting')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddRoomClick();
                }}
                size="small"
                button={'primary'}>
                <Icon
                  icon="plus"
                  color="white"
                  className="size-4 flex-shrink-0"
                />

                <span className="truncate">{t('translation:AddRoom')}</span>
              </Button>
            </div>
          )}
        </div>

        <IconButton
          onClick={handleInfoClick}
          icon="info"
          iconButton="tertiary"
          weight="regular"
          className="rounded-lg"
        />
      </div>
    </div>
  );
};
